<template>
  <form
    v-if="preference.country && preference.language"
    ref="multiStoreForm"
    :action="action"
    method="post"
  >
    <input
      name="_method"
      type="hidden"
      value="put"
    >

    <input
      name="return_to"
      type="hidden"
      :value="returnTo"
    >

    <input
      name="country_code"
      type="hidden"
      :value="preference.country"
    >

    <input
      name="language_code"
      type="hidden"
      :value="preference.language"
    >
  </form>
</template>

<script>

/**
 * Vue: Multi-store (multi-store)
 * -----------------------------------------------------------------------------
 * Used to read country, language, and flag data from section Liquid.
 *
 * @param {Object} current - Current Shopify localization country and language.
 * @param {Array} instances - Shopify instances.
 * @param {Array} theCountries - Countries to prefix with 'the'.
 *
 */
import { mapActions, mapState } from 'vuex'

import query from '~/graphql/queries/countries.gql'

import { formatCountries } from '~/helpers/format'
import { multiStoreAction, multiStoreReturnTo } from '~/helpers/general'

export default {
  name: 'MultiStore',

  props: {
    current: {
      type: Object,
      default: () => ({
        country: {},
        language: {},
      }),
    },
    instances: {
      type: Array,
      default: () => [],
    },
    theCountries: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      storageKey: 'multi-store-supported',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      geolocation: (state) => state['multi-store'].geolocation,
      preference: (state) => state['multi-store'].preference,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.preferenceObject?.instance)
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return multiStoreReturnTo(this.preferenceObject)
    },

    /**
     * Compute preference object.
     * @returns {Object}
     */
    preferenceObject() {
      const preference = this.supported[this.preference.country]

      const matchedLanguage = preference?.languages.find((language) => {
        return language.code === this.preference.language
      })

      return {
        country: preference?.country,
        instance: preference?.instance,
        language: matchedLanguage,
      }
    },

    /**
     * Compute store to show data from.
     * - In case the current country isn't supported we suggest the current
     *   store so the form isn't blank.
     * @returns {Object}
     */
    store() {
      return this.suggestedStore.country
        ? this.suggestedStore
        : this.current
    },

    /**
     * Compute suggested store.
     * @returns {Object}
     */
    suggestedStore() {
      let countryCode =
        cnvs.settings.multiStoreCountry ||
        this.geolocation.country

      if (!cnvs.settings.disableMultiStorePreferences && this.preference) {
        countryCode = this.preference.country
      }

      const matchedStore = this.supported[countryCode]

      const primaryLanguage = matchedStore?.languages
        ? matchedStore.languages[0]
        : this.current.language

      return {
        country: matchedStore?.country,
        instance: matchedStore?.instance,
        language: primaryLanguage,
      }
    },
  },

  watch: {

    /**
     * Watch geolocation.
     */
    geolocation() {
      if (cnvs.settings.alwaysDisplayMultiStore) {
        this.triggerModal()
        return
      }

      if (this.geolocation.country?.toLowerCase() === this.current.country.code?.toLowerCase()) {
        return
      }

      this.triggerModal()
    },

    /**
     * Watch store to update Vuex state.
     */
    store() {
      this.setSuggestedStore(this.store)
    },
  },

  async mounted() {
    this.setCurrent(this.current)
    await this.queryCountries()
    await this.readUrlParameters()

    /**
     * Check if preferences have been set.
     */
    if (!cnvs.settings.disableMultiStorePreferences && this.preference) {
      if (
        !Shopify.designMode &&
        (
          this.preference.country?.toLowerCase() !== this.current.country.code?.toLowerCase() ||
          this.preference.language?.toLowerCase() !== this.current.language.code?.toLowerCase()
        )
      ) {
        this.$refs.multiStoreForm?.submit()
      }

      return
    }

    /**
     * If modal has been dismissed in current session then don't show again,
     * unless country no longer matches the dismissed state.
     */
    if (
      !cnvs.settings.alwaysDisplayMultiStore &&
      sessionStorage.getItem('multi-store-dismissed')
    ) {
      const data = JSON.parse(sessionStorage.getItem('multi-store-dismissed'))

      if (data.country.toLowerCase() === this.current.country.code.toLowerCase()) {
        return
      }
    }

    /**
     * Otherwise query geolocation.
     */
    this.queryGeolocation()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      queueModal: 'overlays/queue',
      setCurrent: 'multi-store/setCurrent',
      setGeolocation: 'multi-store/setGeolocation',
      setPreference: 'multi-store/setPreference',
      setSuggestedStore: 'multi-store/setSuggestedStore',
      setSupported: 'multi-store/setSupported',
    }),

    /**
     * Query countries from storefront API.
     */
    queryCountries() {
      return new Promise(async(resolve, reject) => {
        try {

          /**
           * Retrieve from sessionStorage if it's been set.
           */
          if (
            !cnvs.nonTransactional.disableSavingCountries &&
            sessionStorage.getItem(this.storageKey)
          ) {
            const supported = JSON.parse(sessionStorage.getItem(this.storageKey))
            this.setSupported(supported)
            resolve()
            return
          }

          let config = false

          /**
           * Use non-transactional credentials to query countries on main store.
           */
          if (
            cnvs.nonTransactional.countries &&
            cnvs.nonTransactional.credentials.store !== '' &&
            cnvs.nonTransactional.credentials.token !== ''
          ) {
            config = cnvs.nonTransactional.credentials
          }

          const response = await cnvs.Query({
            config,
            query,
            variables: {
              country: cnvs.store.country,
              language: cnvs.store.language.toUpperCase().split('-')[0],
            },
          })

          const supported = formatCountries(response, {
            instances: this.instances,
            theCountries: this.theCountries,
          })

          this.setSupported(supported)

          if (!cnvs.nonTransactional.disableSavingCountries) {
            sessionStorage.setItem(this.storageKey, JSON.stringify(supported))
          }

          resolve()

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Reads URL parameters if they exist.
     * - Used to set preference to prevent modal from opening if being directed
     *   to an external Shopify instance.
     */
    readUrlParameters() {
      return new Promise(async(resolve) => {
        const parameters = new URLSearchParams(location.search)

        if (!parameters.get('country') && !parameters.get('language')) {
          resolve()
          return
        }

        this.setPreference({
          country: parameters.get('country'),
          language: parameters.get('language'),
        })

        await this.$nextTick()
        resolve()
      })
    },

    /**
     * Query geolocation from Shopify geolocation app.
     */
    async queryGeolocation() {
      try {
        if (cnvs.storybook) {
          this.setGeolocation({
            detected_values: {
              country: {
                handle: 'GB',
                name: 'United Kingdom',
              },
            },
          })

          return
        }

        let response = await fetch('/browsing_context_suggestions.json')
        response = await response.json()
        this.setGeolocation(response)

      } catch (error) {
        throw new Error('Failed to fetch geolocation', error)
      }
    },

    /**
     * Trigger multi-store modal.
     */
    triggerModal() {
      this.queueModal({
        component: 'multi-store-modal',
        ignoreDismissed: true,
        namespace: 'multiStoreModal',
      })
    },
  },
}
</script>
